/**
 * AcceptOnBehalfDialog — "Nhận ca hộ …?" (QĐ 0439, CEO chốt 20/10/2026).
 *
 * Từ ca chụp 21/10/2026, thợ chụp / thợ trang điểm chưa nhận ca thì ca đó không
 * có lương. Thợ đi làm thật mà quên bấm thì Trưởng cửa hàng / Giám đốc nhận hộ
 * ở đây, BẮT BUỘC ghi lý do — máy lưu tên người nhận hộ và lý do cạnh dấu nhận
 * ca để soát lương tra lại được. Cùng khuôn hộp "Gỡ … khỏi buổi?".
 */
import { useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/dialog";
import { Button } from "@/ui/button";
import { Label } from "@/ui/label";
import { Textarea } from "@/ui/textarea";
import { HintNote } from "@/components/HintTip";

/** Lý do ngắn nhất — khớp minProxyReasonRunes của máy chủ. */
export const PROXY_REASON_MIN = 5;

type Props = {
  /** Tên thợ được nhận hộ; null = hộp đóng. */
  personName: string | null;
  /** Dòng phụ: vai · giờ · ngày · khách. */
  context: string;
  pending: boolean;
  onConfirm: (reason: string) => void;
  onOpenChange: (open: boolean) => void;
};

export function AcceptOnBehalfDialog({ personName, context, pending, onConfirm, onOpenChange }: Props) {
  const [reason, setReason] = useState("");

  function change(open: boolean) {
    if (!open) setReason("");
    onOpenChange(open);
  }

  const name = personName ?? "";
  const ok = [...reason.trim()].length >= PROXY_REASON_MIN;

  return (
    <Dialog open={personName !== null} onOpenChange={change}>
      <DialogContent className="max-w-md content-start">
        <DialogHeader>
          <DialogTitle>Nhận ca hộ {name}?</DialogTitle>
          <DialogDescription>{context}</DialogDescription>
        </DialogHeader>

        <p className="rounded-md bg-[var(--color-warning-bg)] px-3 py-2 text-xs text-[var(--color-warning-text)]">
          Chưa nhận ca thì ca này không được tính lương cho {name}. Chỉ nhận hộ khi {name} đã thật sự đi ca này.
        </p>

        <div className="space-y-1.5">
          <Label htmlFor="accept-on-behalf-reason">Lý do nhận hộ (bắt buộc)</Label>
          <Textarea
            id="accept-on-behalf-reason"
            value={reason}
            maxLength={500}
            disabled={pending}
            placeholder="VD: đã đi ca đúng giờ, máy hết pin nên không bấm được"
            onChange={(e) => setReason(e.target.value)}
          />
          <HintNote>Máy lưu tên bạn và lý do này cạnh dấu nhận ca.</HintNote>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => change(false)}>
            Huỷ
          </Button>
          <Button disabled={pending || !ok} onClick={() => onConfirm(reason.trim())}>
            Nhận ca hộ
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
